import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { coursesAPI } from '../services/api';
import { useAuth } from '../utils/AuthContext';
import { PersonCircle, Power, Book, CheckCircle, Bullseye, Plus } from 'react-bootstrap-icons';

const Dashboard = () => {
  const [courses, setCourses] = useState([]);
  const [progress, setProgress] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(true);
  const { user, logout, loading: authLoading, refreshUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const initDashboard = async () => {
      setDashboardLoading(true);
      
      // Check if coming from payment
      const paymentCompleted = localStorage.getItem('payment_completed');
      
      if (paymentCompleted === 'true') {
        // Remove flag
        localStorage.removeItem('payment_completed');
        
        // Force complete refresh of user data
        if (refreshUser) {
          await refreshUser();
          // Wait for state update
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
      
      // Now load dashboard
      if (user?.packageId) {
        await loadDashboard();
      } else {
        setDashboardLoading(false);
      }
    };
    
    if (!authLoading) {
      initDashboard();
    }
  }, [authLoading, user?.packageId]);

  const loadDashboard = async () => {
    try {
      const [coursesRes, progressRes] = await Promise.all([
        coursesAPI.getMyCourses(),
        coursesAPI.getProgress()
      ]);
      setCourses(coursesRes.data);
      setProgress(progressRes.data);
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setDashboardLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleCourseClick = (courseId) => {
    navigate(`/course/${courseId}`);
  };

  if (authLoading || dashboardLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Lädt...</p>
        </div>
      </div>
    );
  }

  if (!user?.packageId) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="text-6xl mb-4">🎸</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Kein Paket aktiv</h2>
          <p className="text-gray-600 mb-6">
            Du hast noch kein Lernpaket. Kaufe jetzt ein Paket um Zugriff auf alle Kurse zu erhalten!
          </p>
          <button
            onClick={() => navigate('/packages')}
            className="w-full bg-gradient-to-r from-primary-600 to-secondary-600 text-white py-3 rounded-lg font-medium hover:from-primary-700 hover:to-secondary-700 transition-all shadow-lg"
          >
            Paket kaufen
          </button>
          <button
            onClick={handleLogout}
            className="w-full mt-3 text-gray-600 hover:text-gray-900 py-2"
          >
            Ausloggen
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50">
      {/* Header - RESPONSIVE */}
      <header className="bg-white shadow-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            {/* Logo */}
            <div className="flex items-center">
              <img 
                src="/logo.png" 
                alt="Guitar Academy Logo" 
                className="h-10 md:h-12"
              />
            </div>
            {/* Buttons */}
            <div className="flex items-center gap-2 sm:gap-4 flex-wrap justify-center">
              <button
                onClick={() => navigate('/profile')}
                className="px-3 sm:px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-all flex items-center space-x-2 text-sm"
              >
                <PersonCircle size={18} />
                <span className="hidden sm:inline">Profil</span>
              </button>
              {user.role === 'admin' && (
                <button
                  onClick={() => navigate('/admin')}
                  className="px-3 sm:px-4 py-2 bg-gradient-to-r from-primary-600 to-secondary-600 text-white rounded-lg font-medium hover:from-primary-700 hover:to-secondary-700 transition-all shadow-lg text-sm"
                >
                  👑 <span className="hidden sm:inline">Admin</span>
                </button>
              )}
              <button
                onClick={handleLogout}
                className="px-3 sm:px-4 py-2 bg-red-100 text-red-700 rounded-lg font-medium hover:bg-red-200 transition-all flex items-center space-x-2 text-sm"
              >
                <Power size={18} />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Message */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-1">
            Hey, {user.firstname || user.email}! 👋
          </h1>
          <p className="text-gray-600">Willkommen zurück - bereit für deine nächste Lektion?</p>
        </div>

        {/* Progress Card ZUERST */}
        {progress && (
          <div className="bg-gradient-to-r from-primary-500 to-secondary-500 rounded-2xl shadow-lg p-8 mb-8 text-white">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-2xl font-bold mb-2">Dein Fortschritt</h2>
                <p className="text-primary-100">
                  {progress.completedLessons} von {progress.totalLessons} Lektionen abgeschlossen
                </p>
              </div>
              <div className="text-5xl font-bold">
                {progress.progressPercent}%
              </div>
            </div>
            <div className="w-full bg-white/20 rounded-full h-3 overflow-hidden">
              <div
                className="bg-white h-full rounded-full transition-all duration-500"
                style={{ width: `${progress.progressPercent}%` }}
              ></div>
            </div>
          </div>
        )}

        {/* KPI Cards DANACH */}
        {progress && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {/* Aktive Kurse */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Aktive Kurse</p>
                  <p className="text-3xl font-bold text-gray-900">{courses.length}</p>
                </div>
                <Book size={40} className="text-primary-600" />
              </div>
            </div>

            {/* Abgeschlossen */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Abgeschlossen</p>
                  <p className="text-3xl font-bold text-green-600">{progress.completedLessons}</p>
                </div>
                <CheckCircle size={40} className="text-green-600" />
              </div>
            </div>

            {/* Noch offen */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Noch offen</p>
                  <p className="text-3xl font-bold text-primary-600">
                    {progress.totalLessons - progress.completedLessons}
                  </p>
                </div>
                <Bullseye size={40} className="text-primary-600" />
              </div>
            </div>
          </div>
        )}

        {/* Courses Grid */}
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Deine Kurse</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Course Cards */}
          {courses.map((course) => (
            <div
              key={course.id}
              onClick={() => handleCourseClick(course.id)}
              className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all cursor-pointer overflow-hidden group border border-gray-100"
            >
              <div className="h-2 bg-gradient-to-r from-primary-500 to-secondary-500"></div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors">
                  {course.title}
                </h3>
                <p className="text-gray-600 mb-4 line-clamp-2">
                  {course.description}
                </p>
                
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">
                    📹 {course.lesson_count} Lektionen
                  </span>
                  <span className="text-primary-600 font-medium">
                    {course.completed_lessons}/{course.lesson_count} ✓
                  </span>
                </div>

                {/* Progress Bar */}
                <div className="mt-4 w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                  <div
                    className="bg-gradient-to-r from-primary-500 to-secondary-500 h-full rounded-full transition-all"
                    style={{
                      width: `${course.lesson_count > 0 ? (course.completed_lessons / course.lesson_count) * 100 : 0}%`
                    }}
                  ></div>
                </div>
              </div>
            </div>
          ))}

          {/* Add Package Card */}
          <div
            onClick={() => navigate('/packages')}
            className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl shadow-lg hover:shadow-xl transition-all cursor-pointer overflow-hidden border-2 border-dashed border-gray-300 hover:border-primary-500 group"
          >
            <div className="h-full flex flex-col items-center justify-center p-6 text-center min-h-[250px]">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-md">
                <Plus size={32} className="text-primary-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors">
                Neues Paket kaufen
              </h3>
              <p className="text-gray-600 text-sm">
                Es gibt noch weitere Pakete, die dich interessieren könnten
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
